import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import lodash from 'lodash';

const withFormikSelect = (SelectComponent) => {
   const InnerWithFormikSelect = (props) => {
      const [errorMessage, setErrorMessage] = useState();
      const [isTouched, setIsTouched] = useState(false);

      useEffect(() => {
         // Used for getting the errorMessage when NumberInput component is used as a FieldArray Field
         const nameParts = props.field?.name?.split(/\]\.|\.|\[|\]/);

         if (nameParts) {
            let errorPtr = props.form.errors;

            if (errorPtr) {
               for (const name of nameParts) {
                  if (lodash.isObject(errorPtr)) {
                     errorPtr = errorPtr[name];
                  } else {
                     break;
                  }
               }
            }

            if (typeof errorPtr === 'string' || typeof errorPtr === 'undefined') {
               setErrorMessage(errorPtr);
            }
         }
      }, [JSON.stringify(props.form.errors)]);

      useEffect(() => {
         if (props.form?.touched && props.field?.name) {
            // This value may be a boolean OR an object with the properties of the value being 'touched' as well
            let isTouched = props.form.touched[props.field.name];

            // Check in hte properties of the touched map.
            // If at least one of the properties is touched, then the base component ('this') is touched
            if (lodash.isObject(isTouched)) {
               for (const [key, value] of Object.entries(isTouched)) {
                  if (value === true) {
                     isTouched = true;
                     break;
                  }
               }
            }

            if (lodash.isBoolean(isTouched)) {
               setIsTouched(isTouched);
            }
         }
      }, [JSON.stringify(props.form.touched)]);

      const handleChange = useCallback((option, arg = { action: undefined }) => {
         props.form.setFieldValue(props.field.name, option);
         if (props?.onChange !== undefined && arg?.action === 'select-option') {
            props?.onChange(
               option,
               {
                  form: {
                     setFieldValue: props.form.setFieldValue,
                  },
               },
            );
         }
      }, [props.form, props.form?.setFieldValue, props.field?.name]);

      const handleBlur = useCallback(() => {
         props.form.setFieldTouched(props.field?.name, true);
      },
      [props.form?.setFieldTouched, props.field?.name]);


      const {field, form, onChange, ...restProp} = props;
      return (
         <SelectComponent
            {...props.field}
            onChange={handleChange}
            onBlur={handleBlur}
            isTouched={isTouched}
            errorMessage={errorMessage}
            {...restProp}
         />
      );
   };

   InnerWithFormikSelect.propTypes = {
      field: PropTypes.shape({
         name: PropTypes.string.isRequired,
         onChange: PropTypes.func.isRequired,
         onBlur: PropTypes.func.isRequired,
         value: PropTypes.oneOfType([
            PropTypes.shape({
               label: PropTypes.string.isRequired,
               value: PropTypes.any,
            }),
            PropTypes.string,
            PropTypes.number,
         ]),
      }).isRequired,
      form: PropTypes.shape({
         errors: PropTypes.object.isRequired,
         touched: PropTypes.object.isRequired,
         setFieldValue: PropTypes.func.isRequired,
         setFieldTouched: PropTypes.func.isRequired,
      }).isRequired,
      isDisabled: PropTypes.bool,
      isSearchable: PropTypes.bool,
      onChange: PropTypes.func,
      isOptionDisabled: PropTypes.func,
      suggestionDependencies: PropTypes.objectOf(PropTypes.any),
      numberChangeToRefresh: PropTypes.number,
   };

   InnerWithFormikSelect.defaultProps = {
      isDisabled: false,
      isSearchable: false,
      onChange: undefined,
      isOptionDisabled: undefined,
      suggestionDependencies: undefined,
      numberChangeToRefresh: undefined,
   };

   return InnerWithFormikSelect;
};

export default withFormikSelect;
